import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import {
  isNumeric,
  isNonEmptyString,
  isNonReductiveNumeric,
} from '../../../js/app/react/validators';
import { extractFirstLongestNumberFromString } from '../../../js/app/react/stringNumericUtils';
import Input, { CENTER, LEFT } from '../Input/InputView';

const WHITESPACE = 3;

const { ADV } = window;

export default class InputPercentage extends Component {
  constructor() {
    super();
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onMeasureWidthRef = this.onMeasureWidthRef.bind(this);
    this.onInputRef = this.onInputRef.bind(this);
    this.onInputPercentageRef = this.onInputPercentageRef.bind(this);
    this.onPostRender = this.onPostRender.bind(this);
    this.state = {
      focused: false,
      textWidth: null,
      inputPaddingLeft: null,
      inputPercentagePaddingLeft: null,
      inputWidth: null,
    };
  }

  componentDidMount() {
    this.onPostRender();
  }

  componentDidUpdate() {
    this.onPostRender();
  }

  onFocus() {
    this.setState({
      focused: true,
    });
  }

  onMeasureWidthRef(measureWidthDOMElement) {
    this.measureWidthDOMElement = measureWidthDOMElement;
  }

  onInputRef(inputDOMElement) {
    this.inputDOMElement = inputDOMElement;
  }

  onInputPercentageRef(inputPercentageDOMElement) {
    this.inputPercentageDOMElement = inputPercentageDOMElement;
  }

  onPostRender() {
    if (this.measureWidthDOMElement) {
      const newTextWidth = parseFloat(ADV.util.getCss(this.measureWidthDOMElement, 'width'));
      if (isNumeric(newTextWidth)) {
        if (this.state.textWidth !== newTextWidth) {
          this.setState({
            textWidth: newTextWidth,
          });
        }
      }
    }

    if (this.inputDOMElement) {
      const newPaddingLeft = parseFloat(ADV.util.getCss(this.inputDOMElement, 'paddingLeft'));
      if (isNumeric(newPaddingLeft)) {
        if (this.state.inputPaddingLeft !== newPaddingLeft) {
          this.setState({
            inputPaddingLeft: newPaddingLeft,
          });
        }
      }

      const newInputWidth = parseFloat(ADV.util.getCss(this.inputDOMElement, 'width'));
      if (isNumeric(newInputWidth)) {
        if (this.state.inputWidth !== newInputWidth) {
          this.setState({
            inputWidth: newInputWidth,
          });
        }
      }
    }

    if (this.inputPercentageDOMElement) {
      const newPaddingLeft = parseFloat(
        ADV.util.getCss(this.inputPercentageDOMElement, 'paddingLeft'),
      );
      if (isNumeric(newPaddingLeft)) {
        if (this.state.inputPercentagePaddingLeft !== newPaddingLeft) {
          this.setState({
            inputPercentagePaddingLeft: newPaddingLeft,
          });
        }
      }
    }
  }

  onBlur(event) {
    this.setState({
      focused: false,
    });
    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
  }

  onChange(eventArg) {
    const { numDecimals } = this.props;
    const event = pick(eventArg, ['target']);
    let amount = event.target.value;
    amount = extractFirstLongestNumberFromString(amount, false, numDecimals > 0, true);
    if (isNonReductiveNumeric(amount)) {
      if (amount <= 100 && amount >= 0) {
        event.target.value = +amount;
        this.props.onChange(event);
      }
    } else {
      event.target.value = amount;
      if (amount <= 100 && amount >= 0) {
        this.props.onChange(event);
      }
    }
    eventArg.preventDefault();
    eventArg.stopPropagation();
  }

  render() {
    const { textWidth, inputPaddingLeft, inputPercentagePaddingLeft, inputWidth } = this.state;
    const { isLeftAligned, placeholder, placeholderClassName, isUnder, id } = this.props;

    const showPercentageGlyph =
      isNumeric(textWidth) &&
      isNumeric(inputPaddingLeft) &&
      isNumeric(inputPercentagePaddingLeft) &&
      isNumeric(inputWidth) &&
      (isNumeric(this.props.value) || isNonEmptyString(this.props.value) || this.state.focused);

    const amount = isNonReductiveNumeric(this.props.value)
      ? this.props.decimalScale !== 3
        ? Math.round(this.props.value * 100) / 100
        : Math.round(this.props.value * 1000) / 1000
      : this.props.value;
    return (
      <div className={`input-percentage ${this.props.className}`} ref={this.onInputPercentageRef}>
        <div className="measure-width" ref={this.onMeasureWidthRef}>
          {amount}
        </div>
        <Input
          isUnder={isUnder}
          alignment={isLeftAligned ? LEFT : CENTER}
          placeholderClassName={placeholderClassName}
          placeholder={placeholder}
          ref={this.onInputRef}
          value={amount}
          maxLength={this.props.maxLength}
          type="number"
          onChange={this.onChange}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          disabled={this.props.disabled}
          id={id}
        />
        {showPercentageGlyph && (
          <i
            style={{
              left: isLeftAligned
                ? Math.min(textWidth, inputWidth) +
                  inputPaddingLeft +
                  inputPercentagePaddingLeft +
                  WHITESPACE
                : inputWidth / 2 +
                  Math.min(textWidth, inputWidth) / 2 +
                  inputPaddingLeft +
                  inputPercentagePaddingLeft +
                  WHITESPACE,
            }}
            id={`${id}-icon`}
          >
            %
          </i>
        )}
      </div>
    );
  }
}

InputPercentage.defaultProps = {
  className: '',
  value: '',
  onBlur: null,
  isLeftAligned: false,
  isUnder: undefined,
  placeholderClassName: '',
  numDecimals: 2,
  disabled: false,
  id: undefined,
  decimalScale: 2,
};

InputPercentage.propTypes = {
  numDecimals: PropTypes.number,
  maxLength: PropTypes.number.isRequired,
  className: PropTypes.string,
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  isUnder: PropTypes.bool,
  placeholderClassName: PropTypes.string,
  isLeftAligned: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  decimalScale: PropTypes.number,
};
